// Cart page functionality start
(function () {
  const ROOT_SELECTOR = '[data-mcc-root]';
  const ERROR_SELECTOR = '[data-mcc-error]';
  const LOADING_SELECTOR = '[data-mcc-loading]';

  const activeRoots = new WeakMap();

  function getMoneyFormat() {
    return window.shopMoneyFormat || window.money_format || '${{amount}}';
  }

  function setCartCount(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;

    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = String(cart.item_count);
    });

    document.querySelectorAll('[data-cart-text]').forEach((node) => {
      node.textContent = cart.item_count === 1 ? 'item' : 'items';
    });
  }

  function setBusy(root, busy) {
    root.classList.toggle('is-loading', busy);
    root.setAttribute('aria-busy', busy ? 'true' : 'false');

    const loadingNode = root.querySelector(LOADING_SELECTOR);
    if (loadingNode) {
      loadingNode.hidden = !busy;
    }
  }

  function showError(root, message) {
    const errorNode = root.querySelector(ERROR_SELECTOR);
    if (!errorNode) return;

    if (message) {
      errorNode.textContent = message;
      errorNode.hidden = false;
    } else {
      errorNode.textContent = '';
      errorNode.hidden = true;
    }
  }

  function parseChildKeys(itemElement) {
    const raw = String(itemElement.dataset.childKeys || '').trim();
    if (!raw) return [];
    return raw.split('|').map((key) => key.trim()).filter(Boolean);
  }

  function getItemElement(target) {
    return target.closest('[data-mcc-cart-item]');
  }

  function getQuantityInput(itemElement) {
    return itemElement.querySelector('[data-mcc-qty-input]');
  }

  function clampQuantity(value) {
    const quantity = Number.parseInt(value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) return 1;
    return quantity;
  }

  async function updateCart(root, updates) {
    const sectionUrl = root.dataset.mccSectionUrl;
    if (!sectionUrl) throw new Error('Cart section URL is missing.');

    setBusy(root, true);
    showError(root, '');

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        let message = 'Unable to update cart.';
        try {
          const errorJson = await response.json();
          message = errorJson && errorJson.description ? errorJson.description : message;
        } catch (error) {
          try {
            const errorText = await response.text();
            if (errorText) message = errorText;
          } catch (readError) {
            // Keep the fallback message.
          }
        }

        throw new Error(message);
      }

      const cart = await response.json();
      setCartCount(cart);
      document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));

      const sectionResponse = await fetch(sectionUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });

      if (!sectionResponse.ok) {
        throw new Error('Cart refreshed, but the page section could not be reloaded.');
      }

      const html = await sectionResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nextRoot = doc.querySelector(ROOT_SELECTOR);

      if (!nextRoot) {
        throw new Error('Cart refreshed, but the rendered section was missing.');
      }

      root.outerHTML = nextRoot.outerHTML;
      initCartPage(document.querySelector(ROOT_SELECTOR));
    } catch (error) {
      showError(root, error && error.message ? error.message : 'Something went wrong while updating the cart.');
    } finally {
      const currentRoot = document.querySelector(ROOT_SELECTOR);
      if (currentRoot) {
        setBusy(currentRoot, false);
      }
    }
  }

  function submitQuantityChange(itemElement, nextQuantity) {
    const root = itemElement.closest(ROOT_SELECTOR);
    if (!root) return;

    const parentKey = String(itemElement.dataset.parentKey || '');
    if (!parentKey) return;

    const updates = {};
    updates[parentKey] = nextQuantity;

    parseChildKeys(itemElement).forEach((childKey) => {
      updates[childKey] = nextQuantity;
    });

    const input = getQuantityInput(itemElement);
    if (input) {
      input.value = String(nextQuantity);
    }

    updateCart(root, updates);
  }

  function removeItem(itemElement) {
    submitQuantityChange(itemElement, 0);
  }

  function handleClick(event) {
    const root = event.currentTarget;
    const itemElement = getItemElement(event.target);
    if (!itemElement) return;

    if (event.target.closest('[data-mcc-remove]')) {
      event.preventDefault();
      removeItem(itemElement);
      return;
    }

    if (event.target.closest('[data-mcc-qty-minus]')) {
      event.preventDefault();
      const input = getQuantityInput(itemElement);
      const nextQuantity = clampQuantity((input ? input.value : 1) - 1);
      submitQuantityChange(itemElement, Math.max(1, nextQuantity));
      return;
    }

    if (event.target.closest('[data-mcc-qty-plus]')) {
      event.preventDefault();
      const input = getQuantityInput(itemElement);
      const nextQuantity = clampQuantity((input ? input.value : 1) + 1);
      submitQuantityChange(itemElement, Math.max(1, nextQuantity));
      return;
    }
  }

  function handleChange(event) {
    const itemElement = getItemElement(event.target);
    if (!itemElement) return;

    if (!event.target.matches('[data-mcc-qty-input]')) return;

    const nextQuantity = clampQuantity(event.target.value);
    submitQuantityChange(itemElement, nextQuantity);
  }

  function initCartPage(root) {
    if (!root || activeRoots.has(root)) return;

    const state = {
      clickHandler: handleClick,
      changeHandler: handleChange
    };

    root.addEventListener('click', state.clickHandler);
    root.addEventListener('change', state.changeHandler);
    activeRoots.set(root, state);
    setBusy(root, false);
    showError(root, '');
  }

  function destroyCartPage(root) {
    const state = activeRoots.get(root);
    if (!state) return;

    root.removeEventListener('click', state.clickHandler);
    root.removeEventListener('change', state.changeHandler);
    activeRoots.delete(root);
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initCartPage);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target && event.target.querySelector ? event.target.querySelector(ROOT_SELECTOR) : null;
    if (root) {
      destroyCartPage(root);
    }
  });
})();
// Cart page functionality end